import { logger } from '@core/logger';
import { getOrchestratorRootUrl } from './orchestratorBaseUrl';
import {
  coerceWebPublicExperience,
  emptyWebPublicExperienceV1,
  type WebPublicExperienceV1,
} from './types/webPublicExperience';

export const BOOKING_UI_PRESETS = [
  'arena',
  'lila',
  'verde',
  'monochrome',
] as const;

export type BookingUiPreset = (typeof BOOKING_UI_PRESETS)[number];

/** Evita que una respuesta tardía pise un preset más reciente (StrictMode / navegación). */
let appearanceApplyGeneration = 0;

export function applyBookingUiPreset(preset: string): void {
  if (typeof document === 'undefined') return;
  const valid = (BOOKING_UI_PRESETS as readonly string[]).includes(preset)
    ? preset
    : 'arena';
  if (valid === 'arena') {
    delete document.documentElement.dataset.wpPreset;
  } else {
    document.documentElement.dataset.wpPreset = valid;
  }
}

export type WebBookingAppearancePayload = {
  preset: BookingUiPreset;
  tenantName: string | null;
  logoUrl: string | null;
  webPublicExperience: WebPublicExperienceV1;
  subdomain: string | null;
};

export function applyWebChromeFromExperience(exp: WebPublicExperienceV1): void {
  if (typeof document === 'undefined') return;
  const fav = exp.branding?.faviconUrl;
  if (fav && /^https?:\/\//i.test(fav)) {
    let link = document.querySelector(
      'link[rel="icon"]',
    ) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = fav;
  }
  const ac = exp.branding?.accentColor;
  if (ac && /^#([0-9a-fA-F]{6})$/.test(ac)) {
    document.documentElement.style.setProperty('--wp-accent', ac);
    document.documentElement.style.setProperty('--wp-primary', ac);
    document.documentElement.dataset.wpCustomPrimary = '1';
  } else {
    document.documentElement.style.removeProperty('--wp-accent');
    document.documentElement.style.removeProperty('--wp-primary');
    delete document.documentElement.dataset.wpCustomPrimary;
  }
}

export function resetWebChromeFromExperience(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.removeProperty('--wp-accent');
  document.documentElement.style.removeProperty('--wp-primary');
  delete document.documentElement.dataset.wpCustomPrimary;
}

/**
 * Apariencia desde orchestrator: preset + marca (nombre y logo del tenant).
 */
export async function fetchWebBookingAppearancePayload(
  tenantId: string,
): Promise<WebBookingAppearancePayload> {
  const empty: WebBookingAppearancePayload = {
    preset: 'arena',
    tenantName: null,
    logoUrl: null,
    webPublicExperience: emptyWebPublicExperienceV1(),
    subdomain: null,
  };
  if (!tenantId?.trim()) return empty;
  try {
    const url = `${getOrchestratorRootUrl()}/api/v1/web/booking/appearance?tenant_id=${encodeURIComponent(tenantId.trim())}`;
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      if (import.meta.env.DEV) {
        logger.warn(
          `fetchWebBookingAppearancePayload HTTP ${res.status} for tenant ${tenantId.trim()}; falling back to arena. Check interaction-orchestrator (GET .../web/booking/appearance).`,
        );
      }
      return empty;
    }
    const data = await res.json();
    const p = data?.bookingUiPreset ?? data?.booking_ui_preset;
    const preset =
      typeof p === 'string' &&
      (BOOKING_UI_PRESETS as readonly string[]).includes(p)
        ? (p as BookingUiPreset)
        : 'arena';
    const rawName = data?.tenantName ?? data?.tenant_name;
    const tenantName =
      typeof rawName === 'string' && rawName.trim() ? rawName.trim() : null;
    const rawLogo = data?.logoUrl ?? data?.logo_url;
    const logoUrl =
      typeof rawLogo === 'string' && rawLogo.trim() ? rawLogo.trim() : null;
    const webPublicExperience = coerceWebPublicExperience(
      data?.webPublicExperience ?? data?.web_public_experience,
    );
    const rawSub = data?.subdomain;
    const subdomain =
      typeof rawSub === 'string' && rawSub.trim() ? rawSub.trim() : null;
    return { preset, tenantName, logoUrl, webPublicExperience, subdomain };
  } catch (err) {
    logger.warn('fetchWebBookingAppearancePayload failed:', err);
    return empty;
  }
}

export async function fetchBookingUiPreset(tenantId: string): Promise<BookingUiPreset> {
  const r = await fetchWebBookingAppearancePayload(tenantId);
  return r.preset;
}

export async function fetchAndApplyBookingUiPreset(
  tenantId: string,
  onBranding?: (b: {
    tenantName: string | null;
    logoUrl: string | null;
    webPublicExperience: WebPublicExperienceV1;
    profileSlug: string | null;
  }) => void,
): Promise<void> {
  const gen = ++appearanceApplyGeneration;
  const { preset, tenantName, logoUrl, webPublicExperience, subdomain } =
    await fetchWebBookingAppearancePayload(tenantId);
  if (gen !== appearanceApplyGeneration) {
    return;
  }
  applyBookingUiPreset(preset);
  applyWebChromeFromExperience(webPublicExperience);
  onBranding?.({ tenantName, logoUrl, webPublicExperience, profileSlug: subdomain });
}
